
import { useState, useEffect, useRef } from 'react';

interface ThemeSummaryItem {
  theme: string;
  count: number;
  latestDate: string | null;
  avgAchievement: number;
  avgProgress: number;
  logs: any[];
}

interface LogThemeSummaryProps {
  logs: any[];
  onThemeClick?: (theme: string) => void;
}

const LOW_ACHIEVEMENT_THRESHOLD = 40;

// ─── 円形プログレスリング ─────────────────────────────────────────────────────
function CircleProgress({
  value,
  color,
  size = 56,
  strokeWidth = 5,
  label,
}: {
  value: number;
  color: string;
  size?: number;
  strokeWidth?: number;
  label: string;
}) {
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const [animated, setAnimated] = useState(0);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    let start: number | null = null;
    const duration = 900;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimated(Math.round(eased * value));
      if (progress < 1) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [value]);

  const animatedOffset = circumference - (animated / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={strokeWidth}
            strokeDasharray={circumference} strokeDashoffset={animatedOffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.05s linear' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold" style={{ color }}>{animated}%</span>
        </div>
      </div>
      <span className="text-[10px] text-slate-500 font-medium">{label}</span>
    </div>
  );
}

// ─── 折れ線グラフ（テーマ別推移） ─────────────────────────────────────────────
function MiniLineChart({ logs, field, color }: { logs: any[]; field: 'achievement_rate' | 'progress_rate'; color: string; }) {
  const sorted = [...logs]
    .filter(l => l[field] != null)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  if (sorted.length < 2) {
    return (
      <div className="flex items-center justify-center h-16 text-xs text-slate-400">
        データが2件以上になるとグラフが表示されます
      </div>
    );
  }

  const values = sorted.map(l => l[field] as number);
  const w = 200; const h = 60; const pad = 6;

  const points = values.map((v, i) => ({
    x: pad + (i / (values.length - 1)) * (w - pad * 2),
    y: h - pad - ((v - 0) / (100 - 0)) * (h - pad * 2),
    v,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x} ${h} L ${points[0].x} ${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${field}-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#grad-${field}-${color.replace('#', '')})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill={color} stroke="white" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

// ─── テーマカード ─────────────────────────────────────────────────────────────
function ThemeSummaryCard({
  item,
  index,
  onThemeClick,
  isLowAchievement,
}: {
  item: ThemeSummaryItem;
  index: number;
  onThemeClick?: (theme: string) => void;
  isLowAchievement: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), index * 80);
    return () => clearTimeout(timer);
  }, [index]);

  const getMotivationMessage = (avg: number) => {
    if (avg >= 85) return { text: '素晴らしい進捗です！🔥', color: 'text-emerald-600' };
    if (avg >= 65) return { text: '順調に進んでいます！😊', color: 'text-teal-600' };
    if (avg >= 45) return { text: 'コツコツ積み上げ中！🙂', color: 'text-amber-600' };
    return { text: 'ここから巻き返そう！💪', color: 'text-rose-600' };
  };

  const motivation = getMotivationMessage((item.avgAchievement + item.avgProgress) / 2);

  // 低達成率の場合のスタイル
  const cardBorderClass = isLowAchievement
    ? 'border-2 border-rose-300 shadow-rose-100 shadow-md'
    : 'border border-gray-200 shadow-sm';

  const headerGradient = isLowAchievement
    ? 'bg-gradient-to-r from-rose-500 via-orange-500 to-rose-500'
    : 'bg-gradient-to-r from-teal-600 to-emerald-600';

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl overflow-hidden transition-all duration-500 ${cardBorderClass} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* 低達成率警告バナー */}
      {isLowAchievement && (
        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center bg-rose-100 rounded-full flex-shrink-0">
            <i className="ri-alert-line text-rose-600 text-xs"></i>
          </div>
          <p className="text-xs font-semibold text-rose-700">
            達成率が低いテーマです — 重点的にフォローアップしましょう
          </p>
          <div className="ml-auto flex-shrink-0">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-bold border border-rose-200 animate-pulse">
              <i className="ri-error-warning-line text-xs"></i>
              要注意
            </span>
          </div>
        </div>
      )}

      {/* カードヘッダー */}
      <div className={`${headerGradient} px-5 py-4 text-white`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-md">
                <i className="ri-lightbulb-line text-xs"></i>
              </div>
              <span className="text-teal-100 text-[10px] font-semibold uppercase tracking-wider">研究テーマ</span>
            </div>
            <h3 className="font-bold text-base leading-snug truncate">{item.theme}</h3>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <CircleProgress
              value={item.avgAchievement}
              color={isLowAchievement ? '#fca5a5' : '#34d399'}
              size={52} strokeWidth={5} label="達成度"
            />
            <CircleProgress
              value={item.avgProgress}
              color={isLowAchievement ? '#fdba74' : '#2dd4bf'}
              size={52} strokeWidth={5} label="進捗度"
            />
          </div>
        </div>
      </div>

      {/* カード本体 */}
      <div className="px-5 py-4">
        {/* 統計バッジ */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-semibold border border-teal-100">
            <i className="ri-file-list-3-line"></i>
            {item.count}件の記録
          </span>
          {item.latestDate && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 text-gray-600 rounded-full text-xs font-medium border border-gray-100">
              <i className="ri-time-line"></i>
              最終: {new Date(item.latestDate).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
            </span>
          )}
          <span className={`text-xs font-semibold ${motivation.color}`}>{motivation.text}</span>
        </div>

        {/* 進捗バー */}
        <div className="space-y-2 mb-4">
          <div>
            <div className="flex justify-between text-[11px] text-slate-500 mb-1">
              <span className="flex items-center gap-1"><i className="ri-trophy-line text-teal-500"></i>平均達成度</span>
              <span className={`font-bold ${isLowAchievement ? 'text-rose-600' : 'text-teal-600'}`}>{item.avgAchievement}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${
                  isLowAchievement
                    ? 'bg-gradient-to-r from-rose-400 to-orange-400'
                    : 'bg-gradient-to-r from-teal-400 to-emerald-500'
                }`}
                style={{ width: `${item.avgAchievement}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-[11px] text-slate-500 mb-1">
              <span className="flex items-center gap-1"><i className="ri-rocket-line text-emerald-500"></i>平均進捗度</span>
              <span className="font-bold text-emerald-600">{item.avgProgress}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-1000"
                style={{ width: `${item.avgProgress}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* 低達成率の場合のアドバイス */}
        {isLowAchievement && (
          <div className="mb-3 p-3 bg-rose-50 border border-rose-100 rounded-xl">
            <p className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1">
              <i className="ri-lightbulb-line"></i>改善のヒント
            </p>
            <ul className="text-xs text-rose-600 space-y-0.5">
              <li className="flex items-start gap-1"><i className="ri-arrow-right-s-line flex-shrink-0 mt-0.5"></i>目標を小さく分割して達成感を積み上げましょう</li>
              <li className="flex items-start gap-1"><i className="ri-arrow-right-s-line flex-shrink-0 mt-0.5"></i>進捗ログを追加して現状を整理しましょう</li>
            </ul>
          </div>
        )}

        {/* グラフ展開ボタン */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-teal-50 rounded-lg text-xs font-semibold text-gray-600 hover:text-teal-700 transition-all cursor-pointer border border-gray-100 hover:border-teal-200"
        >
          <span className="flex items-center gap-1.5">
            <i className="ri-line-chart-line"></i>
            推移グラフを{expanded ? '閉じる' : '見る'}
          </span>
          <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-base`}></i>
        </button>

        {/* 推移グラフ */}
        {expanded && (
          <div className="mt-3 space-y-3 animate-fade-in-up">
            <div className="bg-teal-50/60 rounded-xl p-3 border border-teal-100">
              <p className="text-[11px] font-bold text-teal-700 mb-1 flex items-center gap-1">
                <i className="ri-trophy-line"></i>目標達成度の推移
              </p>
              <MiniLineChart logs={item.logs} field="achievement_rate" color={isLowAchievement ? '#f43f5e' : '#0d9488'} />
            </div>
            <div className="bg-emerald-50/60 rounded-xl p-3 border border-emerald-100">
              <p className="text-[11px] font-bold text-emerald-700 mb-1 flex items-center gap-1">
                <i className="ri-rocket-line"></i>研究進捗度の推移
              </p>
              <MiniLineChart logs={item.logs} field="progress_rate" color="#059669" />
            </div>
          </div>
        )}

        {/* アクションボタン */}
        {onThemeClick && (
          <button
            onClick={() => onThemeClick(item.theme)}
            className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md cursor-pointer ${
              isLowAchievement
                ? 'bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600'
                : 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600'
            }`}
          >
            <i className="ri-add-line"></i>
            {isLowAchievement ? 'このテーマを立て直す' : 'このテーマに進捗を追加'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── メインコンポーネント ─────────────────────────────────────────────────────
export default function LogThemeSummary({ logs, onThemeClick }: LogThemeSummaryProps) {
  const summaries: ThemeSummaryItem[] = (() => {
    const map = new Map<string, any[]>();
    for (const log of logs) {
      const theme = log.research_theme || '（テーマ未設定）';
      if (!map.has(theme)) map.set(theme, []);
      map.get(theme)!.push(log);
    }
    return Array.from(map.entries()).map(([theme, themeLogs]) => {
      const sorted = [...themeLogs].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const avgAchievement = Math.round(
        themeLogs.reduce((s, l) => s + (l.achievement_rate ?? 0), 0) / themeLogs.length
      );
      const avgProgress = Math.round(
        themeLogs.reduce((s, l) => s + (l.progress_rate ?? 0), 0) / themeLogs.length
      );
      return { theme, count: themeLogs.length, latestDate: sorted[0]?.created_at ?? null, avgAchievement, avgProgress, logs: sorted };
    }).sort((a, b) => b.count - a.count);
  })();

  const lowAchievementThemes = summaries.filter(s => s.avgAchievement < LOW_ACHIEVEMENT_THRESHOLD);

  if (summaries.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <i className="ri-bar-chart-line text-3xl text-gray-300"></i>
        </div>
        <p className="text-gray-500 text-sm">進捗ログがまだありません</p>
        <p className="text-gray-400 text-xs mt-1">進捗ログを記録するとテーマ別サマリーが表示されます</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl shadow-sm">
            <i className="ri-pie-chart-2-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">テーマ別進捗サマリー</h3>
            <p className="text-xs text-gray-500">{summaries.length}テーマ・合計{logs.length}件の記録</p>
          </div>
        </div>
        {/* 低達成率テーマ数バッジ */}
        {lowAchievementThemes.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-full">
            <i className="ri-alert-line text-rose-600 text-sm"></i>
            <span className="text-xs font-bold text-rose-700">
              要注意テーマ: {lowAchievementThemes.length}件
            </span>
          </div>
        )}
      </div>

      {/* 低達成率テーマを先頭に表示 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 低達成率テーマを先頭に */}
        {[
          ...summaries.filter(s => s.avgAchievement < LOW_ACHIEVEMENT_THRESHOLD),
          ...summaries.filter(s => s.avgAchievement >= LOW_ACHIEVEMENT_THRESHOLD),
        ].map((item, i) => (
          <ThemeSummaryCard
            key={item.theme}
            item={item}
            index={i}
            onThemeClick={onThemeClick}
            isLowAchievement={item.avgAchievement < LOW_ACHIEVEMENT_THRESHOLD}
          />
        ))}
      </div>
    </div>
  );
}
